import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { useNavigate } from 'react-router-dom';

import type { MermaidNodeBinding } from '../../services/docs/types';

interface MermaidRendererProps {
  source: string;
  bindings: MermaidNodeBinding[];
  className?: string;
}

// Initialize mermaid once
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'strict',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
  },
});

let renderCounter = 0;

const MermaidRenderer: React.FC<MermaidRendererProps> = ({
  source,
  bindings,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const handleNodeClick = useCallback(
    (nodeId: string) => {
      const binding = bindings.find((b) => b.nodeId === nodeId);
      if (!binding) return;

      if (binding.targetType === 'process') {
        navigate(`/docs/processes/${binding.targetId}`);
      } else if (binding.targetType === 'procedure') {
        navigate(`/docs/procedures/${binding.targetId}`);
      }
    },
    [bindings, navigate]
  );

  useEffect(() => {
    if (!containerRef.current || !source.trim()) return;

    const renderDiagram = async () => {
      try {
        setError(null);
        const id = `mermaid-diagram-${++renderCounter}`;
        const { svg } = await mermaid.render(id, source);

        if (!containerRef.current) return;
        containerRef.current.innerHTML = svg;

        // Attach click handlers to nodes that have bindings
        for (const binding of bindings) {
          // Mermaid generates node elements with specific patterns
          const nodeEl =
            containerRef.current.querySelector(
              `[id*="flowchart-${binding.nodeId}-"]`
            ) ??
            containerRef.current.querySelector(`[id*="${binding.nodeId}"]`) ??
            containerRef.current.querySelector(`g.node#${binding.nodeId}`);

          if (nodeEl) {
            const el = nodeEl as HTMLElement;
            el.style.cursor = 'pointer';
            el.setAttribute('tabindex', '0');
            el.setAttribute('role', 'link');
            el.setAttribute(
              'aria-label',
              binding.labelOverride ??
                `Navigate to ${binding.targetType}: ${binding.targetId}`
            );

            el.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNodeClick(binding.nodeId);
            });

            el.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNodeClick(binding.nodeId);
              }
            });

            // Visual hover state
            el.addEventListener('mouseenter', () => {
              el.style.opacity = '0.8';
            });
            el.addEventListener('mouseleave', () => {
              el.style.opacity = '1';
            });
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to render diagram'
        );
      }
    };

    renderDiagram();
  }, [source, bindings, handleNodeClick]);

  if (!source.trim()) {
    return (
      <div className="text-gray-400 text-sm py-4 text-center">
        No diagram defined.
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        <p className="font-medium">Diagram rendering error</p>
        <p className="mt-1 text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`overflow-x-auto ${className ?? ''}`} />
  );
};

export default MermaidRenderer;
